import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Activity,
  ChevronRight,
  FilePlus2,
  HelpCircle,
  Loader2,
  PencilLine,
  Stethoscope,
  TrendingUp,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useActivePatient } from '@/context/ActivePatientContext'
import { useAuthStore } from '@/hooks/useAuthStore'
import { usePatientConsultations } from '@/hooks/usePatientConsultations'
import { VitalsTrendChart } from '@/components/consultation/VitalsTrendChart'
import { ConsultationNoteDialog } from '@/components/consultation/ConsultationNoteDialog'
import { ProgressNoteCard } from '@/components/consultation/ProgressNoteCard'
import { DiagnosisTimeline } from '@/components/consultation/DiagnosisTimeline'
import { EVOLUTION_CONFIG } from '@/components/consultation/EvolutionStatus'
import { NewProgressNoteDialog } from '@/components/consultation/NewProgressNoteDialog'
import type { ConsultationSummary } from '@/services/consultation.service'

/**
 * Progress Notes (Notas de Evolución) — the patient's chronological record of
 * visits. Each signed note is immutable; corrections are append-only clarifying
 * notes. Above the list sits an evolution dashboard: overall trend indicator,
 * vital-sign trends, and a diagnosis timeline.
 */
export function ProgressNotesTab() {
  const { activePatientData } = useActivePatient()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const { signed, drafts, total, isLoading } = usePatientConsultations(activePatientData?.id)
  const [openNoteId, setOpenNoteId] = useState<string | null>(null)
  const [newNoteOpen, setNewNoteOpen] = useState(false)

  // Chronological visit numbers: oldest signed note = #1.
  const visitNumberById = useMemo(() => {
    const chrono = [...signed].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    )
    const map: Record<string, number> = {}
    chrono.forEach((c, i) => {
      map[c.id] = i + 1
    })
    return map
  }, [signed])

  if (!activePatientData) return null
  const patientId = activePatientData.id
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  const resumeDraft = (draft: ConsultationSummary) =>
    navigate(`/workspace/patient/${patientId}/diagnostico?appointment=${draft.appointment_id}`)

  // Overall indicator = most recent signed note's evolution mark.
  const latestStatus = signed[0]?.evolution_status ?? null
  const overall = latestStatus ? EVOLUTION_CONFIG[latestStatus] : null
  const OverallIcon = overall?.icon ?? Activity

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-headline italic text-ml-primary">Notas de Evolución</h2>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-ml-on-surface-variant/60 hover:text-ml-primary"
                >
                  <HelpCircle className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4" align="start">
                <p className="text-xs text-ml-on-surface">
                  Cada visita agrega una nota de evolución al final, en orden cronológico (la más
                  reciente arriba). Las notas firmadas son inmutables: no se editan ni se borran; las
                  correcciones se hacen mediante notas aclaratorias (addendums). Arriba se muestra el
                  resumen de evolución: indicador general, tendencia de signos vitales y línea de
                  tiempo de diagnósticos.
                </p>
              </PopoverContent>
            </Popover>
          </div>
          <p className="mt-1 text-sm text-ml-on-surface-variant">
            Evolución clínica de {activePatientData.full_name}
          </p>
        </div>
        <Button onClick={() => setNewNoteOpen(true)} className="flex-shrink-0">
          <FilePlus2 className="mr-2 h-4 w-4" />
          Registrar nueva nota
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-ml-primary" />
        </div>
      ) : (
        <>
          {/* ── Evolution summary dashboard ──────────────────────────── */}
          <section className="rounded-2xl border border-ml-outline-variant/15 bg-ml-surface-container-lowest p-5">
            {/* Header row: title + compact overall-status badge */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-ml-primary">
                <TrendingUp className="h-4 w-4" />
                Resumen de evolución
              </h3>
              <div
                className={
                  'flex items-center gap-2.5 rounded-xl border px-3 py-2 ' +
                  (overall ? overall.ring : 'border-ml-outline-variant/20 bg-ml-surface text-ml-on-surface-variant')
                }
                title={
                  overall
                    ? 'Según la marca del médico en la nota más reciente.'
                    : 'El médico aún no ha marcado la evolución en una nota firmada.'
                }
              >
                <OverallIcon className="h-4 w-4 shrink-0" />
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-wider opacity-70">
                    Estado general
                  </p>
                  <p className="text-sm font-bold leading-tight">
                    {overall ? overall.label : 'Sin marcar'}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {/* Vital-sign trends */}
              <div>
                <h4 className="mb-3 text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant">
                  Tendencia de signos vitales
                </h4>
                <VitalsTrendChart patientId={patientId} onOpenNote={setOpenNoteId} />
              </div>
              {/* Diagnosis timeline */}
              <div>
                <h4 className="mb-3 text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant">
                  Línea de tiempo de diagnósticos
                </h4>
                <DiagnosisTimeline notes={signed} />
              </div>
            </div>
          </section>

          {/* ── Unsigned drafts (resumable, not part of the record) ──── */}
          {drafts.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-xs font-bold uppercase tracking-widest text-amber-700/80">
                Borradores sin firmar ({drafts.length})
              </h3>
              {drafts.map((d) => {
                const dt = new Date(d.created_at)
                return (
                  <button
                    key={d.id}
                    onClick={() => resumeDraft(d)}
                    className="group flex w-full items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-left transition-all hover:border-amber-300"
                  >
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
                      <PencilLine className="h-5 w-5 text-amber-700" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-xs text-amber-800">
                        {dt.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
                        {' · '}
                        {dt.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {d.chief_complaint && (
                        <p className="mt-1 line-clamp-1 text-sm text-ml-on-surface">{d.chief_complaint}</p>
                      )}
                    </div>
                    <span className="flex flex-shrink-0 items-center gap-1 text-xs font-medium text-amber-700">
                      Continuar <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </button>
                )
              })}
            </section>
          )}

          {/* ── Signed progress notes (most recent first) ────────────── */}
          <section className="space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-widest text-ml-on-surface-variant">
              Notas de evolución firmadas
              {total > 0 && (
                <span className="ml-2 font-normal normal-case tracking-normal text-ml-on-surface-variant/70">
                  ({total} {total === 1 ? 'visita' : 'visitas'})
                </span>
              )}
            </h3>
            {signed.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Stethoscope className="mb-5 h-14 w-14 text-ml-on-surface-variant/20" />
                <h4 className="mb-2 text-xl font-headline italic text-ml-primary">
                  Sin notas firmadas
                </h4>
                <p className="max-w-md text-sm text-ml-on-surface-variant">
                  Aún no hay notas de evolución firmadas. Inicia una consulta en «Diagnóstico» para
                  registrar la primera visita.
                </p>
              </div>
            ) : (
              signed.map((note, i) => (
                <ProgressNoteCard
                  key={note.id}
                  note={note}
                  visitNumber={visitNumberById[note.id] ?? total - i}
                  isAuthor={note.doctor_id === user?.id}
                  isAdmin={isAdmin}
                  defaultOpen={i === 0}
                />
              ))
            )}
          </section>

          {/* Shared read-only viewer for trend-point clicks. */}
          <ConsultationNoteDialog consultationId={openNoteId} onClose={() => setOpenNoteId(null)} />
        </>
      )}

      {/* Inline progress note creation sheet. */}
      <NewProgressNoteDialog
        patientId={patientId}
        open={newNoteOpen}
        onOpenChange={setNewNoteOpen}
        latestNote={signed[0] ?? null}
      />
    </div>
  )
}
